<template>
  <!-- 표 블록 — columns/rows (+rowNav 행 클릭 네비). sticky 헤더, 상태 pill, 숫자 우측 정렬 -->
  <div class="ab-table">
    <div class="ab-table__caption">
      <span v-if="data.caption" class="ab-table__caption-text">{{ data.caption }}</span>
      <span v-if="rows.length" class="ab-table__count">{{ $t('assistant.tableRowCount', { n: rows.length }) }}</span>
    </div>
    <div class="ab-table__scroll">
      <table>
        <thead>
          <tr>
            <th v-for="(col, i) in columns" :key="i" :class="{ 'ab-table__num': numericCols[i] }">{{ col }}</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(row, r) in rows"
            :key="r"
            :class="{ 'ab-table__row--nav': !!navFor(r) }"
            :title="navFor(r) ? $t('assistant.openDetail') : null"
            @click="onRowClick(r)"
          >
            <td v-for="(col, c) in columns" :key="c" :class="{ 'ab-table__num': numericCols[c] }">
              <status-pill v-if="isPill(row[c])" :value="row[c]" />
              <span v-else class="ab-table__cell" :title="cellText(row[c])">{{ cellText(row[c]) }}</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <!-- 더보기 — 표본이 전체보다 적을 때 목록 화면(접수현황관리, 권한 없으면 접수이력)으로 필터와 함께 이동 -->
    <button v-if="more" type="button" class="ab-table__more" @click="onMore">
      {{ moreLabel }} <span class="ab-table__more-arrow">→</span>
    </button>
  </div>
</template>

<script>
import StatusPill from '../StatusPill.vue'
import { resolveStatusPill } from '../useStatusPill'
import { useAssistantNav } from '../useAssistantNav'

/**
 * @component TableBlock
 * @description data.columns(헤더) + data.rows(2차원 배열) 표 렌더 (PRODUCT_UX §2.5.8).
 *   - 상태값 셀은 StatusPill 자동 렌더 (접수/정산/상담 상태 → semantic 색)
 *   - 숫자 열 우측 정렬 + tabular-nums, 셀 말줄임 + title 툴팁, sticky 헤더
 *   - data.rowNav[r]({entity,id,params})가 있으면 행 클릭 → 기존 상세 화면 이동 (OD-13)
 */
export default {
  name: 'TableBlock',
  components: { StatusPill },
  props: {
    data: { type: Object, default: () => ({}) }
  },
  setup() {
    return { ...useAssistantNav() }
  },
  data() {
    return { canManagement: true } // 권한 미확정 시 낙관 기본 → mounted 에서 갱신
  },
  async mounted() {
    // 더보기 문구/이동 대상(접수현황관리 vs 접수이력)을 사용자 권한으로 결정
    this.canManagement = await this.canAccessManagement()
  },
  computed: {
    columns() { return Array.isArray(this.data.columns) ? this.data.columns : [] },
    rows() { return Array.isArray(this.data.rows) ? this.data.rows : [] },
    /** 더보기 메타 — {entity:'repairList', total?, filters?}. 유효 object 일 때만 버튼 노출 */
    more() {
      const m = this.data.more
      return m && typeof m === 'object' && m.entity === 'repairList' ? m : null
    },
    /** 버튼 문구 — 권한별 대상 화면명 + 전체 건수(total). label 은 BE 가 보내지 않음 */
    moreLabel() {
      const screen = this.canManagement
        ? this.$t('assistant.screenManagement')
        : this.$t('assistant.screenHistory')
      const n = this.more && this.more.total
      return (n != null && n !== '')
        ? this.$t('assistant.viewAllIn', { screen, n })
        : this.$t('assistant.viewAllInNoCount', { screen })
    },
    /**
     * 열 전체(빈 값 제외)가 짧은 숫자형이면 우측 정렬 대상.
     * 10자리 이상 연속 숫자(접수번호 등 식별자)는 좌측 정렬 유지.
     */
    numericCols() {
      const isNum = (v) => {
        const s = String(v == null ? '' : v).trim()
        if (!s) return null // 빈 값은 판정 제외
        return /^-?\d{1,3}(,\d{3})*(\.\d+)?%?$/.test(s) || (/^\d+$/.test(s) && s.length <= 9)
      }
      return this.columns.map((_, c) => {
        const flags = this.rows.map((row) => isNum(row[c])).filter((f) => f !== null)
        return flags.length > 0 && flags.every(Boolean)
      })
    }
  },
  methods: {
    cellText(v) { return v == null ? '' : String(v) },
    isPill(v) { return !!resolveStatusPill(v) },
    /** r행의 nav 메타 — rowNav 배열(행별 {entity,id,params} 또는 null) */
    navFor(r) {
      const nav = Array.isArray(this.data.rowNav) ? this.data.rowNav[r] : null
      return nav && typeof nav === 'object' ? nav : null
    },
    async onRowClick(r) {
      const nav = this.navFor(r)
      if (nav) await this.navigate(nav)
    },
    async onMore() {
      if (this.more) await this.navigateList(this.more)
    }
  }
}
</script>

<style scoped>
.ab-table__caption {
  display: flex;
  align-items: baseline;
  gap: 6px;
  margin-bottom: 4px;
}
.ab-table__caption-text { font-size: 12.5px; font-weight: 600; color: var(--color-gray-600, #5f6368); }
.ab-table__count { font-size: 11.5px; color: var(--color-gray-500, #5C616B); }
.ab-table__scroll {
  width: 100%;
  max-width: 100%;
  max-height: 320px;
  overflow: auto;
  border: 1px solid var(--color-gray-200, #e0e0e0);
  border-radius: var(--radius-md, 8px);
  background: var(--color-white, #fff);
}
.ab-table table { border-collapse: separate; border-spacing: 0; width: 100%; font-size: 12.5px; }
.ab-table th, .ab-table td {
  border-bottom: 1px solid var(--color-gray-200, #e0e0e0);
  padding: 6px 10px;
  text-align: left;
  white-space: nowrap;
}
.ab-table tbody tr:last-child td { border-bottom: none; }
.ab-table th {
  position: sticky;
  top: 0;
  z-index: 1;
  background: var(--color-gray-50, #F2F3F5);
  color: var(--color-gray-800, #1E2228);
  font-weight: 600;
}
.ab-table tbody tr:hover td { background: var(--color-gray-50, #F2F3F5); }
/* 네비 가능 행 — hover 시 primary 톤 + 포인터 (클릭 가능 시각 피드백) */
.ab-table__row--nav { cursor: pointer; }
.ab-table__row--nav:hover td { background: var(--color-primary-50, #E8EDF4); }
.ab-table__more {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-top: 6px;
  padding: 5px 12px;
  border: 1px solid var(--color-primary-500, #3B5998);
  background: var(--color-white, #fff);
  color: var(--color-primary-600, #2C4578);
  border-radius: var(--radius-full, 9999px);
  font-size: 12.5px;
  font-weight: 600;
  cursor: pointer;
}
.ab-table__more:hover { background: var(--color-primary-50, #E8EDF4); }
.ab-table__more-arrow { font-weight: 700; }
.ab-table__num { text-align: right; font-variant-numeric: tabular-nums; }
.ab-table__cell {
  display: inline-block;
  max-width: 180px;
  overflow: hidden;
  text-overflow: ellipsis;
  vertical-align: bottom;
}
</style>
